/**
 * Athlete App - Challenges Page
 * Participate in team challenges with real leaderboards
 */

import { useState, useEffect } from 'react';
import {
  listChallenges,
  getUserBadges,
  getChallengeLeaderboard,
  getChallengeActivities,
  useUser,
  type Organization,
  type Challenge,
  type ChallengeProgress,
  type ChallengeActivity,
  type UserBadge,
} from '@tss/shared';
import { Trophy, Target, Calendar, Users, Medal, Flame, Star, ChevronRight, ChevronDown, ChevronUp, Loader2, X, Award, TrendingUp, Activity } from 'lucide-react';
import { formatShortDate, formatRelativeTime } from '@tss/shared';
import { PageHeader, PageSection, TileGrid, ChallengeTile, EmptyState, Skeleton, Badge, StatCard } from '../components/UI';

interface ChallengesPageProps {
  org: Organization | null;
  proxyAthleteId?: string;
}

export function ChallengesPage({ org, proxyAthleteId }: ChallengesPageProps) {
  const { userId } = useUser();
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [badges, setBadges] = useState<UserBadge[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedChallenge, setSelectedChallenge] = useState<Challenge | null>(null);

  // Use proxy athlete ID when in proxy mode
  const effectiveUserId = proxyAthleteId || userId;

  useEffect(() => {
    if (org) {
      loadChallengesData();
    } else {
      setLoading(false);
    }
  }, [org, proxyAthleteId]);

  async function loadChallengesData() {
    if (!org) return;
    setLoading(true);
    try {
      const [challengesData, badgesData] = await Promise.all([
        listChallenges(org.org_id).catch(() => ({ challenges: [] })),
        getUserBadges(proxyAthleteId).catch(() => ({ badges: [] })),
      ]);
      setChallenges(challengesData.challenges || []);
      setBadges(badgesData.badges || []);
    } catch (err) {
      console.error('Failed to load challenges:', err);
    } finally {
      setLoading(false);
    }
  }

  const activeChallenges = challenges.filter((c) => c.status === 'ACTIVE');
  const upcomingChallenges = challenges.filter((c) => c.status === 'UPCOMING');
  const completedChallenges = challenges.filter((c) => c.status === 'COMPLETED');
  const badgeCount = badges.length;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      {/* Header */}
      <PageHeader
        title="Challenges"
        description="Compete with your teammates and track your progress"
        icon={Trophy}
        iconColor="#f59e0b"
      />

      {/* Stats */}
      <div
        className="grid gap-4 mb-6"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))' }}
      >
        <StatCard
          value={activeChallenges.length.toString()}
          label="Active"
          color="var(--success)"
          icon={Target}
        />
        <StatCard
          value={badgeCount.toString()}
          label="Badges Earned"
          color="var(--warning)"
          icon={Medal}
        />
        <StatCard
          value={completedChallenges.length.toString()}
          label="Completed"
          color="var(--accent)"
          icon={Trophy}
        />
      </div>

      {loading ? (
        <Skeleton.ChallengeCard count={4} />
      ) : challenges.length === 0 ? (
        <EmptyState.Challenges />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {activeChallenges.length > 0 && (
            <PageSection
              title="Active Challenges"
              icon={Flame}
              iconColor="#f59e0b"
              count={activeChallenges.length}
            >
              <TileGrid
                items={activeChallenges}
                keyExtractor={(c) => c.challenge_id}
                renderTile={(challenge) => (
                  <ChallengeTile
                    challenge={challenge}
                    currentUserId={effectiveUserId}
                    onClick={() => setSelectedChallenge(challenge)}
                  />
                )}
                showViewToggle={false}
              />
            </PageSection>
          )}

          {upcomingChallenges.length > 0 && (
            <PageSection
              title="Coming Soon"
              icon={Star}
              iconColor="#3b82f6"
              count={upcomingChallenges.length}
            >
              <TileGrid
                items={upcomingChallenges}
                keyExtractor={(c) => c.challenge_id}
                renderTile={(challenge) => (
                  <ChallengeTile
                    challenge={challenge}
                    currentUserId={effectiveUserId}
                    onClick={() => setSelectedChallenge(challenge)}
                  />
                )}
                showViewToggle={false}
              />
            </PageSection>
          )}

          {completedChallenges.length > 0 && (
            <PageSection
              title="Completed"
              icon={Trophy}
              iconColor="#22c55e"
              count={completedChallenges.length}
            >
              <TileGrid
                items={completedChallenges}
                keyExtractor={(c) => c.challenge_id}
                renderTile={(challenge) => (
                  <ChallengeTile
                    challenge={challenge}
                    currentUserId={effectiveUserId}
                    variant="compact"
                    onClick={() => setSelectedChallenge(challenge)}
                  />
                )}
                showViewToggle={false}
              />
            </PageSection>
          )}

          {/* Badges section */}
          {badges.length > 0 && (
            <PageSection
              title="Your Badges"
              icon={Award}
              iconColor="#f59e0b"
              count={badges.length}
            >
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))' }}
              >
                {badges.map((badge) => (
                  <BadgeCard key={badge.badge_id} badge={badge} />
                ))}
              </div>
            </PageSection>
          )}
        </div>
      )}

      {/* Challenge Detail Modal */}
      {selectedChallenge && org && (
        <ChallengeDetailModal
          challenge={selectedChallenge}
          orgId={org.org_id}
          userId={effectiveUserId}
          onClose={() => setSelectedChallenge(null)}
        />
      )}
    </div>
  );
}

function BadgeCard({ badge }: { badge: UserBadge }) {
  return (
    <div
      className="card"
      style={{
        padding: '16px',
        textAlign: 'center',
        background: `linear-gradient(135deg, ${badge.color}15 0%, ${badge.color}05 100%)`,
        border: `1px solid ${badge.color}30`,
      }}
    >
      <div
        style={{
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: badge.color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 12px',
          fontSize: '24px',
        }}
      >
        {badge.icon || '🏆'}
      </div>
      <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '4px' }}>
        {badge.name}
      </h4>
      <p style={{ fontSize: '11px', color: 'var(--gray-500)' }}>
        {formatRelativeTime(badge.earned_at)}
      </p>
    </div>
  );
}

interface ChallengeDetailModalProps {
  challenge: Challenge;
  orgId: string;
  userId: string | null;
  onClose: () => void;
}

function ChallengeDetailModal({ challenge, orgId, userId, onClose }: ChallengeDetailModalProps) {
  const [leaderboard, setLeaderboard] = useState<ChallengeProgress[]>([]);
  const [activities, setActivities] = useState<ChallengeActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'leaderboard' | 'activity'>('leaderboard');

  // Determine if the current user won this challenge
  const winnerId = challenge.is_raffle ? challenge.raffle_winner_id : challenge.winner_id;
  const winnerName = challenge.is_raffle ? challenge.raffle_winner_name : null;
  const isCurrentUserWinner = userId && winnerId === userId;
  const hasWinner = !!winnerId;

  useEffect(() => {
    loadChallengeData();
  }, [challenge.challenge_id]);

  async function loadChallengeData() {
    setLoading(true);
    try {
      const [leaderboardData, activitiesData] = await Promise.all([
        getChallengeLeaderboard(orgId, challenge.challenge_id).catch(() => ({ leaderboard: [] })),
        getChallengeActivities(orgId, challenge.challenge_id).catch(() => ({ activities: [] })),
      ]);
      setLeaderboard(leaderboardData.leaderboard || []);
      setActivities(activitiesData.activities || []);
    } catch (err) {
      console.error('Failed to load challenge data:', err);
    } finally {
      setLoading(false);
    }
  }

  // Find current user's position in the challenge
  // For INDIVIDUAL challenges: match participant_id to userId
  // For TEAM challenges: check if userId is in the breakdown.by_user
  const myProgress = leaderboard.find(entry => {
    if (!userId) return false;

    if (challenge.scope === 'INDIVIDUAL') {
      return entry.participant_id === userId;
    } else {
      // For team challenges, check if user contributed to this team
      const byUser = (entry.breakdown as Record<string, Record<string, number>> | undefined)?.by_user;
      return byUser && userId in byUser;
    }
  });

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: 'min(600px, calc(100vw - 32px))',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px', borderBottom: '1px solid var(--gray-100)' }}>
          <div className="flex-between" style={{ marginBottom: '12px' }}>
            <div>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '4px' }}>
                {challenge.title}
              </h2>
              <p style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
                {formatShortDate(challenge.start_date)} - {formatShortDate(challenge.end_date)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="btn-ghost"
              style={{ padding: '8px', boxShadow: 'none' }}
            >
              <X size={20} />
            </button>
          </div>

          {/* Winner Banner - You Won! */}
          {isCurrentUserWinner && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                padding: '16px',
                marginBottom: '16px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
                borderRadius: 'var(--radius-md)',
                color: 'white',
              }}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Trophy size={28} />
              </div>
              <div>
                <p style={{ fontWeight: '700', fontSize: '18px', marginBottom: '2px' }}>
                  🎉 Congratulations! You Won!
                </p>
                <p style={{ fontSize: '13px', opacity: 0.9 }}>
                  {challenge.is_raffle ? 'You were selected as the raffle winner!' : 'You finished first in this challenge!'}
                </p>
                {challenge.prize_description && (
                  <p style={{ fontSize: '12px', marginTop: '4px', opacity: 0.85 }}>
                    Prize: {challenge.prize_description}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Winner Announcement - Someone else won */}
          {challenge.status === 'COMPLETED' && hasWinner && !isCurrentUserWinner && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px',
                marginBottom: '16px',
                background: 'var(--gray-100)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              <Trophy size={20} style={{ color: 'var(--warning)' }} />
              <div>
                <p style={{ fontWeight: '600', fontSize: '14px', color: 'var(--gray-900)' }}>
                  Winner: {winnerName || 'Announced'}
                </p>
                {challenge.prize_description && (
                  <p style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                    Prize: {challenge.prize_description}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Metric info */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              padding: '12px',
              background: 'var(--gray-50)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '11px', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '4px' }}>
                Metric
              </p>
              <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-900)' }}>
                {challenge.metric.replace(/_/g, ' ')}
              </p>
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '11px', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '4px' }}>
                Participants
              </p>
              <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-900)' }}>
                {challenge.participant_ids.length}
              </p>
            </div>
            {challenge.target_value && (
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '11px', color: 'var(--gray-500)', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Target
                </p>
                <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gray-900)' }}>
                  {challenge.target_value}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--gray-100)' }}>
          <button
            onClick={() => setActiveTab('leaderboard')}
            style={{
              flex: 1,
              padding: '12px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'leaderboard' ? '2px solid var(--accent)' : '2px solid transparent',
              color: activeTab === 'leaderboard' ? 'var(--accent)' : 'var(--gray-500)',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <TrendingUp size={16} />
            Leaderboard
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            style={{
              flex: 1,
              padding: '12px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'activity' ? '2px solid var(--accent)' : '2px solid transparent',
              color: activeTab === 'activity' ? 'var(--accent)' : 'var(--gray-500)',
              fontWeight: '600',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
            }}
          >
            <Activity size={16} />
            Activity
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {loading ? (
            <div className="flex-center" style={{ padding: '40px' }}>
              <div
                className="animate-spin"
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: '3px solid var(--gray-200)',
                  borderTopColor: 'var(--accent)',
                }}
              />
            </div>
          ) : activeTab === 'leaderboard' ? (
            <div>
              {/* User's Position Banner */}
              {myProgress && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '16px',
                    marginBottom: '16px',
                    background: 'linear-gradient(135deg, var(--accent) 0%, #7c3aed 100%)',
                    borderRadius: 'var(--radius-md)',
                    color: 'white',
                  }}
                >
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: 'rgba(255,255,255,0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '700',
                      fontSize: '20px',
                    }}
                  >
                    #{myProgress.rank}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontWeight: '600', fontSize: '16px', marginBottom: '2px' }}>Your Position</p>
                    <p style={{ fontSize: '13px', opacity: 0.9 }}>
                      {myProgress.current_value} points
                      {myProgress.current_streak > 0 && ` • ${myProgress.current_streak} day streak`}
                    </p>
                  </div>
                  <Trophy size={24} style={{ opacity: 0.8 }} />
                </div>
              )}

              {leaderboard.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
                  <TrendingUp size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <p>No rankings yet. Be the first to log activity!</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {leaderboard.map((entry, index) => {
                    const isCurrentUser = myProgress?.participant_id === entry.participant_id;
                    return (
                    <div
                      key={entry.participant_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        background: isCurrentUser
                          ? 'rgba(139, 92, 246, 0.1)'
                          : index < 3
                            ? `rgba(245, 158, 11, ${0.1 - index * 0.03})`
                            : 'var(--gray-50)',
                        borderRadius: 'var(--radius-md)',
                        border: isCurrentUser
                          ? '2px solid var(--accent)'
                          : index === 0
                            ? '1px solid rgba(245, 158, 11, 0.3)'
                            : 'none',
                      }}
                    >
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: index === 0 ? 'var(--warning)' : index === 1 ? '#94a3b8' : index === 2 ? '#b45309' : 'var(--gray-300)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: '700',
                          fontSize: '14px',
                        }}
                      >
                        {entry.rank}
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontWeight: '600', color: 'var(--gray-900)', fontSize: '14px' }}>
                          {entry.participant_name}
                        </p>
                        {entry.current_streak > 0 && (
                          <p style={{ fontSize: '12px', color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Flame size={12} /> {entry.current_streak} day streak
                          </p>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontWeight: '700', color: 'var(--gray-900)', fontSize: '16px' }}>
                          {entry.current_value}
                        </p>
                        <p style={{ fontSize: '11px', color: 'var(--gray-500)' }}>points</p>
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div>
              {activities.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-500)' }}>
                  <Activity size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
                  <p>No activity recorded yet.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {activities.map((activity) => (
                    <div
                      key={activity.activity_id}
                      style={{
                        padding: '12px',
                        background: 'var(--gray-50)',
                        borderRadius: 'var(--radius-md)',
                      }}
                    >
                      <div className="flex-between" style={{ marginBottom: '4px' }}>
                        <p style={{ fontWeight: '600', color: 'var(--gray-900)', fontSize: '14px' }}>
                          {activity.user_name}
                        </p>
                        <Badge variant="success" size="xs" pill uppercase={false}>
                          +{activity.points_earned}
                        </Badge>
                      </div>
                      <p style={{ fontSize: '13px', color: 'var(--gray-600)', marginBottom: '4px' }}>
                        {activity.description}
                      </p>
                      <p style={{ fontSize: '11px', color: 'var(--gray-400)' }}>
                        {formatRelativeTime(activity.created_at)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
